import React from 'react';

import type { colors } from '../../../theme/axiom-theme';
import { shadows } from '../../../theme/axiom-theme';
import type { cssValue } from '../../../sb-helpers/attrs-helper';
import { AttrsHelper } from '../../../sb-helpers/attrs-helper';
import { ColorUtil } from '../../../utils/color-util';

export type LayoutItemProps = {
  background?: keyof typeof colors | CssBackgroundImage;
  border?: cssValue; // TODO APCORE-1551 Make this work: `${string}px ${string} ${keyof typeof colors}`
  borderLeft?: cssValue;
  borderRight?: cssValue;
  borderTop?: cssValue;
  borderBottom?: cssValue;
  borderRadius?: cssValue;
  bottomGutter?: cssValue;
  boxShadow?: keyof typeof shadows;
  children?: React.ReactNode;
  className?: string;
  fluid?: boolean;
  horizontalGutter?: cssValue;
  hug?: boolean;
  leftGutter?: cssValue;
  name?: string;
  /**
   * Lets this item scroll rather than grow, **along the axis its `Layout` runs on** - `overflow-y`
   * inside a vertical layout, `overflow-x` inside a horizontal one. It also gives up its automatic
   * minimum on that axis, which is what makes a flex item scroll instead of pushing its container
   * open.
   */
  overflow?: 'scroll';
  /**
   * Hands this item the whole line and puts it **ahead of siblings that precede it in the DOM** -
   * the full basis is what forces the line break, the reorder is what keeps the siblings together
   * on the next line instead of stranding one above and one below. Only a wrapping horizontal
   * `Layout` has lines to give, so nothing else answers to it.
   */
  fullWidth?: boolean;
  position?: 'left' | 'right' | 'center';
  rightGutter?: cssValue;
  topGutter?: cssValue;
  verticalGutter?: cssValue;
  /**
   * Fills the row's height **and stacks this item's own children vertically** - an item has no
   * `direction` of its own, so unlike `Layout`'s `stretched` the column here does apply.
   */
  stretched?: boolean;
};

export const LayoutItem = ({
  background,
  border,
  borderLeft,
  borderRight,
  borderTop,
  borderBottom,
  borderRadius,
  bottomGutter,
  boxShadow,
  children,
  className,
  fluid,
  horizontalGutter,
  hug,
  leftGutter,
  name,
  overflow,
  fullWidth,
  position = 'left',
  rightGutter,
  topGutter,
  verticalGutter,
  stretched,
}: LayoutItemProps) => {
  const backgroundImage =
    background && typeof background === 'object' ? background : undefined;
  const styles = {
    backgroundImage:
      backgroundImage?.image && `url("${backgroundImage.image}")`,
    backgroundAttachment: backgroundImage?.attachment,
    backgroundClip: backgroundImage?.clip,
    backgroundColor: backgroundImage?.color,
    backgroundPosition:
      backgroundImage && (backgroundImage.position || 'right center'),
    backgroundRepeat:
      backgroundImage && (backgroundImage.repeat || 'no-repeat'),
    borderLeft: AttrsHelper.replaceColorNameWithHex(borderLeft || border),
    borderRight: AttrsHelper.replaceColorNameWithHex(borderRight || border),
    borderTop: AttrsHelper.replaceColorNameWithHex(borderTop || border),
    borderBottom: AttrsHelper.replaceColorNameWithHex(borderBottom || border),
    boxShadow: boxShadow && shadows[boxShadow],
    borderRadius: AttrsHelper.sanitizeCssValue(borderRadius),
    paddingBottom: AttrsHelper.sanitizeCssValue(bottomGutter || verticalGutter),
    paddingLeft: AttrsHelper.sanitizeCssValue(leftGutter || horizontalGutter),
    paddingRight: AttrsHelper.sanitizeCssValue(rightGutter || horizontalGutter),
    paddingTop: AttrsHelper.sanitizeCssValue(topGutter || verticalGutter),
    textAlign: position,
  };
  return (
    <div
      className={AttrsHelper.formatClassname(
        'layout-item',
        fluid && 'fluid-item',
        hug && 'hug-item',
        overflow && `overflow-${overflow}`,
        fullWidth && 'full-width-item',
        stretched && 'layout-stretched',
        // The image form keeps the inline style; there is no token for a url.
        typeof background === 'string' &&
          ColorUtil.tokenClass({ color: background, prefix: 'background' }),
        className
      )}
      data-test={name}
      style={styles}
    >
      {children}
    </div>
  );
};
